import { CRM_KEYTERMS, normalizeCrm } from './genieBrain';

/**
 * Browser helpers for the Genie voice call.
 * No Deepgram key is used here. Audio goes to your backend (/chat/voice/*), which talks to Deepgram.
 */

/** Turns an HTML answer into something short and natural to read aloud. */
export const toSpokenText = (html, max = 420) => {
    const doc = new DOMParser().parseFromString(html || '', 'text/html');
    const hadDetail = !!doc.querySelector('table, ul, ol');
    doc.querySelectorAll('table').forEach((t) => t.remove());        // tables do not read well aloud
    const blocks = Array.from(doc.body.querySelectorAll('p, li, h1, h2, h3, h4'))
        .map((el) => (el.textContent || '').replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .map((t) => (/[.!?]$/.test(t) ? t : `${t}.`));
    const text = (blocks.length ? blocks.join(' ') : (doc.body.textContent || '').replace(/\s+/g, ' ').trim());
    if (!text) return hadDetail ? 'I put the details in the chat.' : '';

    const sentences = text.match(/[^.!?]+[.!?]+(\s|$)/g) || [text];
    let out = '';
    let cut = false;
    sentences.some((s) => {
        if ((out + s).length > max && out) { cut = true; return true; }
        out += s;
        return false;
    });
    out = out.trim().slice(0, max + 80);
    return cut || hadDetail ? `${out} I put the full details in the chat.` : out;
};

const pickMime = () => {
    if (typeof MediaRecorder === 'undefined') return '';
    return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((m) => MediaRecorder.isTypeSupported(m)) || '';
};

/**
 * Records one utterance at a time and ends it automatically after a short silence.
 * listenOnce() resolves with an audio Blob, or null when nothing worth sending was heard.
 */
export function createListener(stream, { silenceMs = 1100, minSpeechMs = 350, maxWaitMs = 20000 } = {}) {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    const mime = pickMime();
    let abortCurrent = null;

    const level = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) { const d = (buf[i] - 128) / 128; sum += d * d; }
        return Math.sqrt(sum / buf.length);
    };

    const listenOnce = ({ onLevel } = {}) => new Promise((resolve) => {
        if (ctx.state === 'suspended') ctx.resume();
        const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
        const chunks = [];
        rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

        let done = false;
        let speaking = false;
        let loudTicks = 0;
        let speechStart = 0;
        let lastLoud = 0;
        const calib = [];
        let floor = 0.01;
        const startedAt = Date.now();

        let timer = null;
        const finish = (keep) => {
            if (done) return;
            done = true;
            clearInterval(timer);
            abortCurrent = null;
            if (rec.state === 'inactive') { resolve(null); return; }
            rec.onstop = () => resolve(keep && chunks.length ? new Blob(chunks, { type: rec.mimeType || mime || 'audio/webm' }) : null);
            rec.stop();
        };
        abortCurrent = () => finish(false);

        timer = setInterval(() => {
            const now = Date.now();
            const lv = level();
            if (onLevel) onLevel(lv);

            if (calib.length < 8) {                        // learn the room noise for the first ~400ms
                calib.push(lv);
                if (calib.length === 8) floor = Math.min(0.03, calib.reduce((a, b) => a + b, 0) / 8);
                return;
            }
            const threshold = Math.min(0.08, Math.max(0.025, floor * 2.5));
            if (lv > threshold) {
                loudTicks += 1;
                lastLoud = now;
                if (!speaking && loudTicks >= 3) { speaking = true; speechStart = now - 150; }
            } else {
                loudTicks = 0;
            }
            if (speaking && now - lastLoud > silenceMs) finish(lastLoud - speechStart >= minSpeechMs);
            else if (!speaking && now - startedAt > maxWaitMs) finish(false);
        }, 50);

        rec.start(100);
    });

    return {
        listenOnce,
        abort: () => { if (abortCurrent) abortCurrent(); },
        close: () => {
            if (abortCurrent) abortCurrent();
            stream.getTracks().forEach((t) => t.stop());
            ctx.close().catch(() => { });
        },
    };
}

/**
 * Plays an audio Blob at `rate` times normal speed (1 = normal). onProgress receives 0..1 while it plays.
 * Returns { promise, stop }; the promise resolves when playback ends or stop() is called.
 */
export function playBlob(blob, onProgress, rate = 1) {
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    const speed = Math.min(2, Math.max(0.8, Number(rate) || 1));   // faster or slower without changing the pitch
    audio.defaultPlaybackRate = speed;
    audio.playbackRate = speed;
    audio.preservesPitch = true;
    audio.webkitPreservesPitch = true;
    let raf = 0;
    let finish = () => { };
    const promise = new Promise((resolve, reject) => {
        const cleanup = () => { cancelAnimationFrame(raf); URL.revokeObjectURL(url); };
        finish = (err) => { cleanup(); if (err) reject(err); else resolve(); };
        audio.onended = () => finish();
        audio.onerror = () => finish(new Error('Audio playback failed'));
        const tick = () => {
            if (onProgress && audio.duration) onProgress(Math.min(1, audio.currentTime / audio.duration));
            raf = requestAnimationFrame(tick);
        };
        audio.play().then(() => { raf = requestAnimationFrame(tick); }).catch((e) => finish(e));
    });
    return { promise, stop: () => { audio.pause(); finish(); } };
}

/**
 * TEMPORARY, development only: calls Deepgram straight from the browser.
 * The key is bundled into the page, so anyone who opens dev tools can read it.
 * Replace with the backend routes in VOICE_SETUP.md before release (then delete this function).
 * Returns the same shape as the backend API: { transcribe(blob) -> { text }, speak(text) -> audio Blob }.
 */
export function createDirectVoiceApi(apiKey, { ttsModel = 'aura-2-apollo-en', sttModel = 'nova-3', language = 'en' } = {}) {
    const auth = { Authorization: `Token ${apiKey}` };
    return {
        direct: true,
        async transcribe(blob) {
            const params = new URLSearchParams({ model: sttModel, language, smart_format: 'true', punctuate: 'true' });
            // Nova-3 keyterm prompting: makes Deepgram listen for CRM and sales words
            if (/^nova-3/.test(sttModel)) CRM_KEYTERMS.forEach((k) => params.append('keyterm', k));
            const res = await fetch(`https://api.deepgram.com/v1/listen?${params}`, {
                method: 'POST',
                headers: { ...auth, 'Content-Type': blob.type || 'audio/webm' },
                body: blob,
            });
            if (!res.ok) throw new Error(`Speech-to-text failed (${res.status})`);
            const json = await res.json();
            const alt = json && json.results && json.results.channels && json.results.channels[0] && json.results.channels[0].alternatives[0];
            return { text: normalizeCrm((alt && alt.transcript) || '').trim() };
        },
        async speak(text) {
            const clean = String(text || '').replace(/\s+/g, ' ').trim().slice(0, 1900);
            const res = await fetch(`https://api.deepgram.com/v1/speak?model=${encodeURIComponent(ttsModel)}&encoding=mp3`, {
                method: 'POST',
                headers: { ...auth, 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: clean }),
            });
            if (!res.ok) throw new Error(`Text-to-speech failed (${res.status})`);
            return res.blob();
        },
    };
}